import { JSX, useCallback, useContext, useEffect } from "react";
import { PageSection } from "@app/shared";
import { QESAlert } from "@app/shared/qes-standards";
import DragAndDropMultiFileUploader from "@app/shared/DragAndDrogMultiFileUploader";
import { SupplementaryDocument } from "@app/projects/results/common/specialtyGroupException";
import { CreateExceptionsContext } from "../contexts/CreateExceptionsContext";
import { DragAndDropMultiFileUploaderContext } from "@app/shared/DragAndDropMultiFileUploaderContext";
import { WizardStepProps } from "../contexts/interfaces";
import ExceptionService from "../exceptionsService";

const CreateExceptionsAttachment = (props: WizardStepProps): JSX.Element => {
  const wizardContext = useContext(CreateExceptionsContext);
  const fileUploaderContext = useContext(DragAndDropMultiFileUploaderContext);
  const uploadDocumentsApiKey: string = "document";
  const exceptionService = new ExceptionService();

  useEffect(() => {
    props.pageIsValid(!wizardContext.loading);
  }, [wizardContext.loading]);

  useEffect(() => {
    if (wizardContext.resetFileUploader) {
      fileUploaderContext.setUploadedFiles(new Map() as Map<number, File>);
      wizardContext.setResetFileUploader(false);
    }
  }, [wizardContext.resetFileUploader])

  const handleFileUpload = useCallback(async (file: File) => {
    try {
      const formData = new FormData();
      formData.append(uploadDocumentsApiKey, file);
      const metadataId = await exceptionService.uploadSupplementaryDocument(formData);
      const fileinfo: FormDataEntryValue = formData.get(uploadDocumentsApiKey);

      if (!!metadataId && fileinfo instanceof File) {
        const supplementaryDocument: SupplementaryDocument = {
          documentName: fileinfo.name ? fileinfo.name : '',
          supplementaryDocumentMetadataId: metadataId,
        };

        wizardContext.setAttachments(prevDocuments => [...prevDocuments, supplementaryDocument]);
        fileUploaderContext.setUploadedFiles(prevFiles => {
          const newFiles = new Map(prevFiles);
          newFiles.set(metadataId, file);
          return newFiles;
        });
      }
    } catch (error) {
      throw error.responseJSON;
    }
  }, [exceptionService, fileUploaderContext]);

  const handleFileRemove = useCallback((documentKey: number) => {
    wizardContext.setAttachments(prevDocuments => prevDocuments.filter(item => item.supplementaryDocumentMetadataId != documentKey));
    fileUploaderContext.setUploadedFiles(prevFiles => {
      const newFiles = new Map(prevFiles);
      newFiles.delete(documentKey);
      return newFiles;
    });
  }, [fileUploaderContext]);

  return (
    <PageSection>
      <div className="mt-3 col">
        <div className="row">
          <div className="col-6">
            <p className="font-weight-bolder">Upload any attachments to this exception. Attachments must be in .pdf, .png, .jpg, .doc, .docx format.</p>
            <QESAlert type="info" persist={true} classNames="mt-3 mb-3">Any document added will be attached to all selected exceptions and projects selected.</QESAlert>

            <DragAndDropMultiFileUploader
              apiKeyName={uploadDocumentsApiKey}
              title=""
              filetype=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onMultipleUpload={handleFileUpload}
              onRemove={handleFileRemove}
              small={false}
              testId="crew-exceptions-attachment-uploader"
            />
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default CreateExceptionsAttachment;
